/**
 * Small form-field primitives for the content editors. There is no Textarea in
 * @fc/ui, so we provide a brand-styled native textarea + labelled field wrapper
 * here (matching the native-input styling used in app-shell.tsx).
 */
import { cn, Label } from '@fc/ui'
import { type ReactNode, type TextareaHTMLAttributes, useId } from 'react'

export function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string
  hint?: string
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-dim">{hint}</p>}
    </div>
  )
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        'min-h-32 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-fg',
        'placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring',
        className,
      )}
      {...props}
    />
  )
}

/** Brand-styled native <select> (no UI primitive yet). */
export function Select({
  id,
  value,
  onChange,
  children,
  className,
}: {
  id?: string
  value: string
  onChange: (v: string) => void
  children: ReactNode
  className?: string
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg',
        'focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
        className,
      )}
    >
      {children}
    </select>
  )
}

/** Generates a stable id for a field/control pair. */
export function useFieldId(prefix: string): string {
  const id = useId()
  return `${prefix}-${id}`
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 160)
}

export function statusBadgeVariant(
  status: 'draft' | 'published' | 'archived',
): 'gold' | 'success' | 'neutral' {
  if (status === 'published') return 'success'
  if (status === 'draft') return 'gold'
  return 'neutral'
}
